import React, { ChangeEvent } from 'react'
import { captureException } from '@sentry/browser'
import clsx from 'clsx'

import { Thumbnail } from 'components'
import { IconAddImage, IconCloseCircle } from '../Icons'
import { resizeImage } from 'helpers/common'

export interface ImageUploadProps {
  /**
   * Image source
   */
  src?: string
  /**
   * On image change
   */
  onChangeMultiple?(e: React.ChangeEvent<HTMLInputElement>): void
  onChange?(blob: Blob): void
  /**
   * On clear image
   */
  onClear?(): void
  isClearable?: boolean
  className?: string
  /**
   * Placeholder popup text in image
   */
  placeholder?: string
  /**
   * Identifier for input
   */
  htmlFor?: string
  name?: string
  isMultiple?: boolean
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  src,
  isClearable,
  className,
  placeholder = 'Placeholder text',
  htmlFor = 'image-input',
  name,
  isMultiple,
  onChange = () => {},
  onChangeMultiple = () => {},
  onClear = () => {},
}: ImageUploadProps): JSX.Element => {
  const base = 'image-upload'
  const cn = clsx(base, 'thumbnail', 'thumbnail--primary', className)
  const iconCn = `${base}__icon`
  const iconCloseCn = clsx(iconCn, `${base}__icon-close`)

  const _onClear = (e: React.SyntheticEvent) => {
    e.preventDefault()
    onClear()
  }

  const setResizeImage = (file: File) => {
    const originalSize = file.size
    const targetSize = 204800 // 200kb

    if (originalSize > targetSize) {
      resizeImage(file, 720, 960).then(
        (blob) => {
          onChange(blob)
        },
        (error) => {
          captureException(error)
        },
      )
    } else {
      file.arrayBuffer().then((arrayBuffer) => {
        const blob = new Blob([new Uint8Array(arrayBuffer)], {
          type: file.type,
        })

        onChange(blob)
      })
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    const fileLength: number = files?.length || 0

    if (files) {
      for (let i = 0; i < fileLength; i++) {
        const file = files[i]
        const fileReader = new FileReader()

        fileReader.onload = () => {
          setResizeImage(file)
        }

        if (typeof file === 'object') {
          fileReader.readAsDataURL(file)
        }
      }
    }
  }

  return (
    <div className={cn}>
      <label className={`${base}__wrapper`} htmlFor={htmlFor}>
        <input
          data-testid='image-input'
          name={name}
          id={htmlFor}
          type='file'
          accept='image/jpeg, image/jpg, image/png'
          onChange={isMultiple ? onChangeMultiple : handleChange}
          multiple={isMultiple}
          hidden
        />
        {!src ? (
          <IconAddImage className={iconCn} variants='lg' />
        ) : (
          <React.Fragment>
            {isClearable && (
              <IconCloseCircle
                className={iconCloseCn}
                onClick={_onClear}
                variants='md'
              />
            )}
            <Thumbnail src={src} className={`${base}__img`} />
            <div className={`${base}__label`}>
              <span>{placeholder}</span>
            </div>
          </React.Fragment>
        )}
      </label>
    </div>
  )
}

export default ImageUpload
